import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { api, money } from "@/lib/api";

const RANGES = [[30, "30 days"], [90, "90 days"], [365, "12 months"], [0, "All time"]];

const Stat = ({ label, value, testId }) => (
  <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-4 sm:p-6 min-w-0" data-testid={testId}>
    <p className="text-[10px] sm:text-xs uppercase tracking-wider text-slate-400 mb-2">{label}</p>
    <p className="num text-xl sm:text-3xl font-bold break-words">{value}</p>
  </div>
);

const ChannelRow = ({ c, max, cur }) => (
  <tr className="border-t border-slate-800" data-testid={`lead-source-${c.key}`}>
    <td className="px-5 py-4">
      <p className="font-semibold">{c.label}</p>
      <div className="mt-2 h-1.5 rounded-full bg-slate-800 overflow-hidden max-w-[220px]"><div className="h-full bg-emerald-500 rounded-full" style={{ width: `${(100 * c.converted) / max}%` }} /></div>
    </td>
    <td data-label="Requests" className="px-5 py-4 num" data-testid={`lead-source-requests-${c.key}`}>{c.requests}</td>
    <td data-label="Converted" className="px-5 py-4 num text-emerald-300" data-testid={`lead-source-converted-${c.key}`}>{c.converted}</td>
    <td data-label="Conversion" className="px-5 py-4 num">{c.conversion_rate}%</td>
    <td data-label="Paying clients" className="px-5 py-4 num">{c.paying}</td>
    <td data-label="Revenue (paid invoices)" className="px-5 py-4 num font-semibold">{money(c.revenue, cur)}</td>
    <td data-label="Deposits paid" className="px-5 py-4 num text-slate-400">{money(c.deposits, cur)}</td>
  </tr>
);

export default function Reports() {
  const [days, setDays] = useState(90);
  const [d, setD] = useState(null);
  const [cur, setCur] = useState("USD");
  useEffect(() => { api.get("/public/pricing").then((r) => setCur(r.data.currency)).catch(() => {}); }, []);
  useEffect(() => { setD(null); api.get("/reports/lead-sources", { params: { days } }).then((r) => setD(r.data)).catch(() => setD({ error: true })); }, [days]);
  const rows = d?.channels?.filter((c) => c.requests > 0) || [];
  const max = Math.max(1, ...rows.map((c) => c.converted));
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 fade-up" data-testid="reports-page">
      <p className="text-xs uppercase tracking-[0.2em] text-emerald-400 mb-2">Reports</p>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-2">Lead sources</h1>
          <p className="text-slate-400 max-w-2xl">Which channels bring requests that become paying clients. Search and social are detected from where a visitor first arrived.</p>
        </div>
        <div className="grid grid-cols-4 sm:flex gap-1 rounded-xl sm:rounded-full border border-slate-800 p-1 w-full sm:w-auto" data-testid="reports-range">
          {RANGES.map(([v, l]) => <button key={v} type="button" onClick={() => setDays(v)} data-testid={`reports-range-${v}`} className={`px-2 sm:px-4 py-1.5 rounded-lg sm:rounded-full text-xs sm:text-sm whitespace-nowrap transition-colors ${days === v ? "bg-emerald-500 text-emerald-950 font-semibold" : "text-slate-400 hover:text-white"}`}>{l}</button>)}
        </div>
      </div>
      {!d ? <Loader2 className="h-6 w-6 animate-spin text-emerald-400" /> : d.error ? <p className="text-rose-300" data-testid="reports-error">Could not load the report. Please refresh.</p> : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-6 mb-10">
            <Stat label="Requests" value={d.total.requests} testId="reports-total-requests" />
            <Stat label="Converted to clients" value={d.total.converted} testId="reports-total-converted" />
            <div className="col-span-2 sm:col-span-1"><Stat label="Revenue from converted" value={money(d.total.revenue, cur)} testId="reports-total-revenue" /></div>
          </div>
          <div className="rounded-xl border border-slate-800 bg-slate-900/60 overflow-x-auto mb-10">
            <table className="w-full text-sm m-cards" data-testid="lead-sources-table">
              <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500">{["Channel", "Requests", "Converted", "Conversion", "Paying clients", "Revenue", "Deposits"].map((h) => <th key={h} className="px-5 py-3 font-medium">{h}</th>)}</tr></thead>
              <tbody>{rows.map((c) => <ChannelRow key={c.key} c={c} max={max} cur={cur} />)}</tbody>
            </table>
            {!rows.length && <p className="px-5 py-8 text-slate-500" data-testid="reports-empty">No requests in this period.</p>}
          </div>
          {d.landing_pages.length > 0 && (
            <section data-testid="reports-landing-pages">
              <h2 className="font-display text-xl font-bold mb-4">Top landing pages</h2>
              <div className="rounded-xl border border-slate-800 divide-y divide-slate-800">
                {d.landing_pages.map((p) => <div key={p.path} className="flex items-center justify-between gap-4 px-5 py-3 text-sm"><span className="truncate text-slate-300">{p.path}</span><span className="num text-slate-400 shrink-0">{p.requests} requests · <span className="text-emerald-300">{p.converted} converted</span></span></div>)}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
